import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteGroupPolicySettings,
  getGroupPolicySettings,
  listGroupPolicies,
  toggleGroupPolicyVersioned,
  updateGroupPolicySettingsVersioned,
} from '@kirin/api-client/api/policies';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import type {
  PolicyModeType,
  PolicySettings,
  PolicySettingsUpdate,
} from '@kirin/api-client/contracts/policies';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

/*
  Security-group-scoped policy hooks (KIR-3800).

  These deliberately mirror the signatures and result shapes of their
  `useOrganizationPolicies` counterparts so `PoliciesList` can be handed either
  set unchanged — the Policies page swaps hook families when the scope selector
  changes, and the list component stays scope-agnostic.

  The group write endpoints support optimistic concurrency via `expectedVersion`.
  We deliberately omit it: the organization scope has always been last-write-wins
  and the Policies UI has no version-conflict affordance to show. Adding one is a
  separate change that should cover both scopes at once, not just this one.
*/

/**
 * Fetch a security group's policies. Each policy carries its effective value and
 * an `isOverridden` flag (group override vs inherited organization default).
 *
 * @param organizationId - The organization ID
 * @param groupId - The security group ID
 * @param enabled - Whether to run the query (default: true when both provided)
 * @returns React Query result with the policies array
 */
export function useGroupPolicies(
  organizationId: OrganizationId | undefined,
  groupId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.policies.group(organizationId ?? '', groupId ?? ''),
    queryFn: async () => {
      if (!organizationId) throw new Error('organizationId is required');
      if (!groupId) throw new Error('groupId is required');
      const result = await listGroupPolicies(
        apiClient,
        organizationId,
        groupId
      );
      return result.policies;
    },
    enabled: enabled && !!organizationId && !!groupId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Fetch a security group's settings for one policy.
 *
 * @param organizationId - The organization ID
 * @param groupId - The security group ID
 * @param policyKey - The policy key
 * @param enabled - Whether to run the query (default: true when all provided)
 * @returns React Query result with the policy settings
 */
export function useGroupPolicySettings(
  organizationId: OrganizationId | undefined,
  groupId: string | undefined,
  policyKey: string | undefined,
  enabled = true
) {
  return useQuery<PolicySettings>({
    queryKey: queryKeys.policies.groupSettings(
      organizationId ?? '',
      groupId ?? '',
      policyKey ?? ''
    ),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      if (!groupId) throw new Error('groupId is required');
      if (!policyKey) throw new Error('policyKey is required');
      return getGroupPolicySettings(
        apiClient,
        organizationId,
        groupId,
        policyKey
      );
    },
    enabled: enabled && !!organizationId && !!groupId && !!policyKey,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}

/**
 * Toggle (or set the mode of) a security group's policy. Writes only to the
 * group scope; the organization's own value is untouched.
 *
 * @param organizationId - The organization ID
 * @param groupId - The security group ID
 * @returns React Query mutation taking `{ policyKey, enabled, mode? }`
 */
export function useToggleGroupPolicy(
  organizationId: OrganizationId | undefined,
  groupId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      policyKey,
      enabled,
      mode,
    }: {
      policyKey: string;
      enabled: boolean;
      mode?: PolicyModeType;
    }) => {
      if (!organizationId) throw new Error('Organization ID is required');
      if (!groupId) throw new Error('Group ID is required');
      return toggleGroupPolicyVersioned(
        apiClient,
        organizationId,
        groupId,
        policyKey,
        enabled,
        mode !== undefined ? { mode } : undefined
      );
    },
    onSuccess: () => {
      if (!organizationId || !groupId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.policies.group(organizationId, groupId),
      });
    },
  });
}

/**
 * Update a security group's policy settings. Writes only to the group scope.
 *
 * @param organizationId - The organization ID
 * @param groupId - The security group ID
 * @returns React Query mutation taking a `PolicySettingsUpdate`
 */
export function useUpdateGroupPolicySettings(
  organizationId: OrganizationId | undefined,
  groupId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settingsUpdate: PolicySettingsUpdate) => {
      if (!organizationId) throw new Error('Organization ID is required');
      if (!groupId) throw new Error('Group ID is required');
      return updateGroupPolicySettingsVersioned(
        apiClient,
        organizationId,
        groupId,
        settingsUpdate
      );
    },
    onSuccess: (_, variables) => {
      if (!organizationId || !groupId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.policies.groupSettings(
          organizationId,
          groupId,
          variables.policyKey
        ),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.policies.group(organizationId, groupId),
      });
    },
  });
}

/**
 * Reset a security group's policy settings so members fall back to the
 * organization default. Omit `settingKey` to reset every setting on the policy.
 *
 * @param organizationId - The organization ID
 * @param groupId - The security group ID
 * @returns React Query mutation taking `{ policyKey, settingKey? }`
 */
export function useResetGroupPolicySettings(
  organizationId: OrganizationId | undefined,
  groupId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      policyKey,
      settingKey,
    }: {
      policyKey: string;
      settingKey?: string;
    }) => {
      if (!organizationId) throw new Error('Organization ID is required');
      if (!groupId) throw new Error('Group ID is required');
      return deleteGroupPolicySettings(
        apiClient,
        organizationId,
        groupId,
        policyKey,
        settingKey
      );
    },
    onSuccess: (_, variables) => {
      if (!organizationId || !groupId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.policies.groupSettings(
          organizationId,
          groupId,
          variables.policyKey
        ),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.policies.group(organizationId, groupId),
      });
    },
  });
}
